import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import clsx from 'clsx'

import { RoutingPaths } from 'app/routes/routes'

import { CloseCircle as ICloseCircle } from 'entities/close-circle/model'
import { DOCUMENT, GENDER } from 'entities/user/module'

import { CloseCircleApi } from 'shared/api/close-circle'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import error_icon from 'shared/images/error.png'
import success_icon from 'shared/images/success_popup.png'
import { showWrongDateFormat } from 'shared/lib/dateFormat'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { Button } from 'shared/ui/button'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { ListBlock } from 'shared/ui/list-block'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import s from './styles.module.scss'

enum RESULT_REQUEST {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export const DetailPerson = () => {
  const { t } = useTranslation()

  const { openDialog, closeDialog } = useDialogBottomSheet()

  const navigate = useNavigate()

  const { id: closeCircleId } = useParams()

  const [personData, setPersonalData] = useState<ICloseCircle>()

  const getCloseCircle = async (id: string) => {
    try {
      const { data: dataCircle } = await CloseCircleApi.getUserDocumentsById(id)

      setPersonalData(dataCircle?.data)
    } catch {
      openDialog({
        child: (
          <PopupContent
            button={t('close')}
            title={t('error')}
            description={t('close_circle.error_user_not_allowed')}
            onClick={() => navigate(-1)}
          />
        ),
        isDialog: false,
      })
    }
  }

  const onEdit = () => {
    navigate(
      RoutingPaths.closeCircle.detailFormDetail.path(String(closeCircleId)),
    )
  }

  const onDeleteHandler = () => {
    openDialog({
      child: (
        <PopupContent
          onClick={onRequestDelete}
          button={t('continue')}
          title={t('close_circle.delete_title')}
          description={t('close_circle.delete_desc')}
        />
      ),
      isDialog: false,
    })
  }

  const onRequestDelete = async () => {
    try {
      await CloseCircleApi.deleteUserDocuments(closeCircleId!)

      showPopUp(RESULT_REQUEST.SUCCESS)
    } catch {
      showPopUp(RESULT_REQUEST.ERROR)
    }
  }

  const showPopUp = (result: RESULT_REQUEST) => {
    openDialog({
      child: (
        <PopupContent
          image={result === RESULT_REQUEST.SUCCESS ? success_icon : error_icon}
          title={
            result === RESULT_REQUEST.SUCCESS
              ? t('success_title')
              : t('error_popup')
          }
          description={
            result === RESULT_REQUEST.SUCCESS
              ? t('close_circle.delete_pop_desc')
              : t('error_popup_description')
          }
          button={t('continue')}
          onClick={() => closePopUp(result === RESULT_REQUEST.SUCCESS)}
        />
      ),
      isDialog: false,
    })
  }

  const closePopUp = (isSuccess?: boolean) => {
    if (isSuccess) {
      navigate(RoutingPaths.closeCircle.path(), { replace: true })
    }

    closeDialog()
  }

  useEffect(() => {
    closeCircleId && getCloseCircle(closeCircleId)
  }, [])

  const typesDocuments = {
    [DOCUMENT.ID]: t('id_passport'),
    [DOCUMENT.PASSPORT]: t('passport'),
    [DOCUMENT.BIRTH_CERTIFICATE]: t('birth_certificate'),
  }

  const optionsGender = {
    [GENDER.MALE]: t('male'),
    [GENDER.FEMALE]: t('female'),
  }

  return (
    <div className={clsx('container', s.container)}>
      {personData ? (
        <div className={s.content}>
          <ListBlock
            list={[
              {
                label: t('saved_as'),
                value: personData?.label,
              },
              {
                label: t('citizenship'),
                value: personData?.citizenship_obj?.country,
              },
              {
                label: t('type_passport'),
                value:
                  personData?.document_type &&
                  typesDocuments[personData?.document_type as DOCUMENT],
              },
              {
                label: t('serial_documents'),
                value: personData?.document_number,
              },
              {
                label: t('expiration_date'),
                value: showWrongDateFormat(
                  personData?.document_expire,
                  DATE_FORMAT_LIST.FULL_DATE,
                ),
              },
              {
                label: t('last_name'),
                value: personData?.last_name,
              },
              {
                label: t('first_name'),
                value: personData?.first_name,
              },
              // {
              //   label: t('middle_name'),
              //   value: personData?.middle_name || '-',
              // },
              {
                label: t('gender'),
                value:
                  personData?.gender &&
                  optionsGender[personData?.gender as GENDER],
              },
              {
                label: t('birth_day'),
                value: showWrongDateFormat(
                  personData?.date_of_birth,
                  DATE_FORMAT_LIST.FULL_DATE,
                ),
              },
            ]}
          />

          <div className={s.removeButton}>
            <Button
              variant="transparent"
              onClick={onDeleteHandler}
            >
              <div className={s.button}>
                <AppIcon
                  id={AllIcons.REMOVE}
                  className={s.icon}
                />
                <span className={s.remove}>{t('close_circle.delete')}</span>
              </div>
            </Button>
          </div>

          <ButtonContinue onClick={onEdit}>
            <span className={s.text}>{t('edit')}</span>
          </ButtonContinue>
        </div>
      ) : (
        <Loader />
      )}
    </div>
  )
}
